import {
  isAuthRetryableFetchError,
  type Session,
  type User as SupabaseAuthUser,
} from '@supabase/supabase-js';

import { supabase } from '@/lib/supabase';
import type { Database, Json } from '@joshua/types/database.generated';
import { ApiError, NotFoundError, isReconnectingError, toApiError } from '@/services/errors';
import { normalizarCanal } from '@/features/permissions/canal';

type Pessoa = Database['public']['Tables']['pessoa']['Row'];
type PessoaFilial = Database['public']['Tables']['pessoa_filial']['Row'];

/**
 * Subset of pessoa fields needed by AuthContext on login. Narrowing the select
 * at the service boundary keeps the context's type surface small and avoids
 * pulling every pessoa column into the auth state.
 */
export type AuthPessoa = Pick<
  Pessoa,
  | 'id'
  | 'nome'
  | 'email'
  | 'perfil_id'
  | 'apelido'
  | 'user_ativo'
  | 'tenant_id'
  | 'config'
>;

export type Escopo = 'own' | 'department' | 'branch' | 'company' | 'platform';

/** Channel a view counts on (JOSH-91). Non-view entries always carry 'ambos'. */
export type Canal = 'web' | 'mobile' | 'ambos';

export interface PermissionEntry {
  recurso: string;
  acao: string;
  escopo: Escopo;
  /** Optional so caches written by older bundles still parse; absent = 'ambos'. */
  canal?: Canal;
}

export type PermissionCache = PermissionEntry[];

export interface PessoaContext {
  tenantId: string | null;
  filialId: string | null;
}

/**
 * Shape of pessoa.config as used by the app. Stored as jsonb in the DB; this
 * type is the structural contract for AuthContext.
 */
export interface PessoaConfigInput {
  tema?: 'light' | 'dark';
  menu_pinado?: boolean;
  home_bg?: {
    tipo: 'preset' | 'cor' | 'imagem';
    valor: string;
    overlay_cor?: string;
    overlay_opacidade?: number;
    filtro?: 'none' | 'grayscale' | 'sepia';
  };
  notificacoes?: {
    email: boolean;
    push: boolean;
  };
}

/**
 * Race a promise against a timeout. Used for pessoa load during cold starts
 * where network latency can wedge React state for long periods.
 */
async function withTimeout<T>(
  promise: PromiseLike<T>,
  timeoutMs: number,
  label: string,
): Promise<T> {
  let timer: ReturnType<typeof setTimeout> | undefined;
  const timeout = new Promise<never>((_, reject) => {
    timer = setTimeout(() => {
      reject(new Error(`${label} timeout after ${timeoutMs / 1000}s`));
    }, timeoutMs);
  });
  try {
    return await Promise.race([promise, timeout]);
  } finally {
    if (timer) clearTimeout(timer);
  }
}

export async function signInWithPassword(
  email: string,
  senha: string,
): Promise<{ user: SupabaseAuthUser; session: Session }> {
  const { data, error } = await supabase.auth.signInWithPassword({
    email,
    password: senha,
  });
  if (error) throw toApiError('Falha ao fazer login', error);
  if (!data.user || !data.user.email || !data.session) {
    throw new ApiError('Sessão inválida após login');
  }
  return { user: data.user, session: data.session };
}

/** auth-js internals used to drop the stored session without a server call. */
interface AuthClientInternals {
  _removeSession?: () => Promise<void>;
}

/** A sign-out failure that says nothing about the session: network, 5xx, 429. */
function isRetryableSignOutError(error: unknown): boolean {
  if (isAuthRetryableFetchError(error)) return true;
  const status = (error as { status?: unknown } | null)?.status;
  return status === 429;
}

/**
 * Signs out. A retryable failure (offline, 5xx, a 429 during a refresh
 * cooldown) never blocks the user (JOSH-89, R16): supabase-js then keeps the
 * stored session, so it is removed here and the call resolves. The server
 * session is not revoked in that case; that is an accepted gap (plan Risks).
 */
export async function signOutFromSupabase(): Promise<void> {
  const { error } = await supabase.auth.signOut();
  if (!error) return;
  if (!isRetryableSignOutError(error)) throw toApiError('Falha ao fazer logout', error);

  console.warn('[auth.service] Server sign-out failed; clearing the session locally.');
  // Cast the object, never detach the method: `_removeSession` uses `this`.
  const auth = supabase.auth as unknown as AuthClientInternals;
  if (typeof auth._removeSession !== 'function') {
    throw toApiError('Falha ao fazer logout', error);
  }
  await auth._removeSession();
}

export async function getCurrentSession(): Promise<Session | null> {
  const { data, error } = await supabase.auth.getSession();
  if (error) throw toApiError('Falha ao recuperar sessão', error);
  return data.session;
}

/**
 * Load the minimum pessoa shape needed to bootstrap AuthContext after login.
 * The person is the one linked to the login (pessoa.user_id), never the one
 * sharing its e-mail (JOSH-51).
 * Throws NotFoundError when pessoa is missing (caller decides to force logout).
 * `timeoutMs` lets callers use a generous budget on cold starts.
 */
export async function getPessoaByUserId(
  userId: string,
  options: { timeoutMs?: number } = {},
): Promise<AuthPessoa> {
  const timeoutMs = options.timeoutMs ?? 30_000;
  try {
    const { data, error } = await withTimeout(
      supabase
        .from('pessoa')
        .select('id, nome, email, perfil_id, apelido, user_ativo, tenant_id, config')
        .eq('user_id', userId)
        .is('deleted_at', null)
        .maybeSingle(),
      timeoutMs,
      'getPessoaByUserId',
    );
    if (error) throw toApiError('Erro ao carregar pessoa', error);
    if (!data) {
      throw new NotFoundError('Pessoa não encontrada para este usuário');
    }
    return data;
  } catch (err) {
    if (err instanceof ApiError) throw err;
    throw toApiError('Erro ao carregar pessoa', err);
  }
}

/**
 * Resolve the multi-tenant context (tenant_id + filial_id) for a pessoa.
 * Preserves the AuthContext fallback chain: try the RPC first (the canonical
 * source of truth), and fall back to a direct pessoa_filial query if the RPC
 * path fails (e.g. during early RLS rollout). Returns null values rather than
 * throwing when the context is simply unavailable — the caller treats "no
 * context" as a warning state, not a login failure.
 */
export async function getPessoaContext(pessoaId: string): Promise<PessoaContext> {
  const { data, error } = await supabase.rpc('get_pessoa_context', {
    p_pessoa_id: pessoaId,
  });
  // JOSH-89: a reconnecting session is a pause, not "no context".
  if (isReconnectingError(error)) throw toApiError('Erro ao carregar contexto', error);

  if (!error && data) {
    const shape = data as { tenant_id?: string | null; filial_id?: string | null };
    return {
      tenantId: shape.tenant_id ?? null,
      filialId: shape.filial_id ?? null,
    };
  }

  const fallback = await supabase
    .from('pessoa_filial')
    .select('filial_id, filial:filial_id (tenant_id)')
    .eq('pessoa_id', pessoaId)
    .is('data_fim', null)
    .limit(1)
    .maybeSingle();

  if (isReconnectingError(fallback.error)) {
    throw toApiError('Erro ao carregar contexto', fallback.error);
  }
  if (fallback.error || !fallback.data) {
    return { tenantId: null, filialId: null };
  }

  const row = fallback.data as Pick<PessoaFilial, 'filial_id'> & {
    filial: { tenant_id: string | null } | null;
  };
  return {
    tenantId: row.filial?.tenant_id ?? null,
    filialId: row.filial_id ?? null,
  };
}

/**
 * Fail-closed permission loader (ADR-004). Reads from pessoa_permissao_cache,
 * which is the single source of truth for effective permissions. Callers decide
 * whether an empty result means "no permissions" or "retry".
 */
export async function loadPermissionCache(pessoaId: string): Promise<PermissionCache> {
  const { data, error } = await supabase
    .from('pessoa_permissao_cache')
    .select('recurso_codigo, acao_codigo, escopo, canal')
    .eq('pessoa_id', pessoaId);

  if (error) throw toApiError('Erro ao carregar permissões', error);

  return (data ?? []).map((row) => ({
    recurso: row.recurso_codigo.toUpperCase(),
    acao: row.acao_codigo.toLowerCase(),
    escopo: row.escopo as Escopo,
    canal: normalizarCanal(row.canal),
  }));
}

/**
 * Server-side resolution of the daily AUTO wallpaper preset. Returns the
 * concrete home_bg the client should render — non-AUTO settings pass through
 * unchanged, AUTO is materialized to a specific preset key for today and the
 * resolution is cached server-side under config.home_bg.cache.
 *
 * Failure is non-fatal: callers fall back to the unresolved config.home_bg.
 */
export async function resolveHomeBg(
  pessoaId: string,
): Promise<PessoaConfigInput['home_bg'] | null> {
  // RPC is not yet in the generated Database types — call through a typed
  // alias of supabase that accepts any function name. We must not detach
  // `supabase.rpc` from its receiver (would lose `this` and silently no-op).
  const sb = supabase as unknown as {
    rpc: (
      fn: string,
      args: Record<string, unknown>,
    ) => PromiseLike<{ data: unknown; error: { message: string } | null }>;
  };
  const { data, error } = await sb.rpc('pessoa_resolve_home_bg', {
    p_pessoa_id: pessoaId,
  });
  if (error) {
    console.warn('[auth.service] resolveHomeBg failed:', error.message);
    return null;
  }
  return (data ?? null) as PessoaConfigInput['home_bg'] | null;
}

/**
 * Persist pessoa.config to DB. Returns the server-side value so the caller
 * doesn't trust an optimistic merge as canonical.
 */
export async function savePessoaConfig(
  pessoaId: string,
  config: PessoaConfigInput,
): Promise<PessoaConfigInput> {
  const { data, error } = await supabase
    .from('pessoa')
    .update({
      config: config as unknown as Json,
      updated_at: new Date().toISOString(),
    })
    .eq('id', pessoaId)
    .select('config')
    .single();

  if (error) throw toApiError('Erro ao salvar configuração do usuário', error);
  return (data.config ?? {}) as unknown as PessoaConfigInput;
}
